import { Button, CircularProgress, createStyles, DialogContent, makeStyles, TextField, Theme } from '@material-ui/core';
import React, { useReducer, useState } from 'react';
import { Node } from 'slate';

import {
  PublishStatus,
  useCreatePostMutation,
  useGetDocumentToImportLazyQuery,
  useGetPlexDetailsQuery,
} from '../../../../graphql/gen/types';
import { operationName } from '../../../../graphql/operationName';
import { GET_POSTS_QUERY } from '../../../../graphql/queries/GetPosts';
import { spacings } from '../../../../theme';
import { notEmpty } from '../../../../utils/notEmpty';
import { PlexName } from '../../common/PlexName';
import { usePlexRoute } from '../../details/usePlexRoute';
import { logAnalytics } from '../../shared/logAnalytics';
import { DialogActions } from '../DialogActions';
import { DialogTopBar } from '../DialogTopBar';
import { CreateOrEditDialogTitle } from '../post/CreateOrEditDialogTitle';
import { useDialogState } from '../useDialogState';
import { documentToImportReducer } from './documentToImportReducer';
import { getSelectedContent } from './getSelectedContent';
import { PostGoogleDocPreview } from './PostGoogleDocPreview';
import { useInitializeDocumentToImportOnSuccess } from './useInitializeDocumentToImportOnSuccess';

type Props = {
  plexId: string;
};

export const ImportPostFromGoogleDocs = ({ plexId }: Props): React.ReactElement => {
  const classes = useStyles();
  const [, setDialogState] = useDialogState();
  const [, setPlexUrlParams] = usePlexRoute();
  const [googleDocLink, setGoogleDocLink] = useState('');
  const { data: plexData } = useGetPlexDetailsQuery({ variables: { plexId } });
  // TODO: error handling
  const [getDocumentToImport, { data, loading }] = useGetDocumentToImportLazyQuery({ fetchPolicy: 'no-cache' });
  // TODO: error handling
  const [createPostMutation, { loading: createPostLoading }] = useCreatePostMutation();
  const [documentToImport, dispatch] = useReducer(documentToImportReducer, undefined);

  const handleImport = async (): Promise<void> => {
    if (documentToImport == null) {
      getDocumentToImport({ variables: { url: googleDocLink } });
    } else {
      const { selectedSections, document, content, externalEntityId } = documentToImport;
      const { titleSection, selectedIndexes } = getSelectedContent({
        document,
        selectedSections: [...selectedSections],
      });
      const { data } = await createPostMutation({
        variables: {
          post: {
            plexId,
            title: Node.string((titleSection.title as unknown) as Node),
            publishStatus: PublishStatus.Internal,
            body: content.filter((_, index) => selectedIndexes[index]),
            labelIds: [],
            source: {
              externalEntityId,
              headingIds: [...selectedSections].map((section) => section.title.headingId).filter(notEmpty),
            },
          },
        },
        refetchQueries: [operationName(GET_POSTS_QUERY)],
        awaitRefetchQueries: true,
      });

      if (data?.createPost != null) {
        const { id, permalink } = data.createPost;

        logAnalytics({ name: 'post imported gdoc', details: { plexId, itemId: id } });
        setDialogState(null, 'replace');
        setPlexUrlParams({ selectedItem: { type: 'post', permalink, commentsVisible: false } }, 'replace');
      }
    }
  };

  useInitializeDocumentToImportOnSuccess({ data, dispatch });

  if (!plexData?.loggedIn?.plexById) {
    return <CircularProgress size={24} />;
  }

  return (
    <div className={classes.root}>
      <DialogTopBar>
        <PlexName
          name={plexData.loggedIn.plexById.name}
          color={plexData.loggedIn.plexById.color}
          className={classes.plexName}
        />
      </DialogTopBar>

      <CreateOrEditDialogTitle />

      <DialogContent>
        <TextField
          value={googleDocLink}
          fullWidth
          variant="outlined"
          label="Google document link"
          name="Google document link"
          placeholder="https://docs.google.com/document/d/.../"
          onChange={(e): void => {
            setGoogleDocLink(e.currentTarget.value);
            dispatch({ type: 'INIT', state: undefined });
          }}
        />

        <PostGoogleDocPreview dispatch={dispatch} documentToImport={documentToImport} data={data} loading={loading} />
      </DialogContent>

      <DialogActions
        cta={
          <Button variant="contained" color="primary" disabled={loading || createPostLoading} onClick={handleImport}>
            {documentToImport == null ? 'Preview' : 'Import'}
          </Button>
        }
      />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    plexName: {
      marginLeft: spacings.xlarge,
      marginTop: spacings.normal,
      marginBottom: spacings.normal,
    },
  })
);
